<template>
    <TransitionRoot
        as="template"
        :show="state.open"
    >
        <Dialog
            as="div"
            class="relative z-6000"
            :open="state.open === true"
            @close="dialogClose(state.overlayPreventClose)"
        >
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div
                    class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                        enter-to="opacity-100 translate-y-0 scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                    >
                        <DialogPanel
                            class="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6"
                        >
                            <div class="absolute top-4 right-3 hidden pt-4 pr-4 sm:block">
                                <button
                                    type="button"
                                    class="cursor-pointer rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-hidden"
                                    @click="dialogClose()"
                                >
                                    <span class="sr-only">Close</span>
                                    <MdiClose
                                        class="h-6 w-6"
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                            <div class="flex items-start">
                                <div
                                    class="mx-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-200 sm:mx-0"
                                >
                                    <MdiImage
                                        class="h-6 w-6 text-gray-600"
                                        aria-hidden="true"
                                    />
                                </div>
                                <div class="pt-1 text-center sm:ml-2 sm:text-left">
                                    <DialogTitle
                                        as="h3"
                                        class="text-base leading-6 font-semibold text-gray-900"
                                    >
                                        {{ state.title }}
                                    </DialogTitle>
                                </div>
                            </div>

                            <div class="flex">
                                <div class="block w-full px-2 py-2">
                                    <label
                                        for="name"
                                        class="block text-sm font-medium text-gray-700 select-none"
                                    >
                                        {{ t('editorInsertImageUrl') }}
                                    </label>

                                    <div class="mt-1 flex">
                                        <select
                                            id="placeholders"
                                            v-model="defaultProtocol.name"
                                            name="placeholders"
                                            class="rounded-l-md border border-r-0 border-gray-300 bg-white font-medium shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                        >
                                            <option
                                                v-for="protocol in allowedProtocols"
                                                :key="protocol"
                                                class="font-medium"
                                                :value="protocol"
                                            >
                                                {{ protocol }}
                                            </option>
                                        </select>
                                        <input
                                            id="name"
                                            v-model="imageSource"
                                            type="text"
                                            name="name"
                                            autocomplete="off"
                                            :class="[
                                                defaultProtocol.name === 'data:'
                                                    ? 'border-r-0'
                                                    : 'rounded-r-md',
                                                'block w-full grow border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm',
                                            ]"
                                            @keyup.enter="handleInsertClick()"
                                        />
                                        <button
                                            v-if="defaultProtocol.name === 'data:'"
                                            v-tippy="t('imageEditorOpenFile')"
                                            class="flex-none cursor-pointer rounded-r-md border border-gray-300 p-1 text-sm text-gray-600 shadow-sm hover:bg-gray-200 hover:text-gray-700"
                                            @click="chooseFile()"
                                        >
                                            <div class="dropzone-file flex items-center space-x-1">
                                                <MdiOpenInApp class="h-4 w-4" />
                                                <span class="font-medium">
                                                    {{ t('imageEditorOpenFile') }}
                                                </span>
                                                <input
                                                    id="image-file"
                                                    type="file"
                                                    accept="image/png,image/jpeg,image/gif"
                                                    class="hidden"
                                                    @change="loadImageFile"
                                                />
                                            </div>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div class="grid grid-cols-1 gap-4 px-2 py-2 md:grid-cols-3">
                                <div>
                                    <label
                                        for="image-alt"
                                        class="block text-sm font-medium text-gray-700 select-none"
                                    >
                                        {{ t('editorInsertImageAlt') }}
                                    </label>
                                    <input
                                        id="image-alt"
                                        v-model="imageAlt"
                                        type="text"
                                        autocomplete="off"
                                        class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                    />
                                </div>
                                <div>
                                    <label
                                        for="image-width"
                                        class="block text-sm font-medium text-gray-700 select-none"
                                    >
                                        {{ t('editorInsertImageWidth') }}
                                    </label>
                                    <input
                                        id="image-width"
                                        v-model.number="imageWidth"
                                        type="number"
                                        min="1"
                                        :max="maxImageWidth()"
                                        autocomplete="off"
                                        class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                    />
                                </div>
                                <div>
                                    <label
                                        for="image-height"
                                        class="block text-sm font-medium text-gray-700 select-none"
                                    >
                                        {{ t('editorInsertImageHeight') }}
                                    </label>
                                    <input
                                        id="image-height"
                                        v-model.number="imageHeight"
                                        type="number"
                                        min="1"
                                        autocomplete="off"
                                        class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                    />
                                </div>
                                <div class="col-span-1 md:col-span-3">
                                    <InsertImageCodeStatic @generated="applyGeneratedImageCode" />
                                </div>
                                <div class="col-span-1 md:col-span-3">
                                    <AlertBox
                                        v-if="!validation.validUrl"
                                        state="danger"
                                        :icon="MdiAlert"
                                        :title="
                                            t('editorInsertImageCannotUse', {
                                                format: defaultProtocol.name,
                                            })
                                        "
                                    >
                                    </AlertBox>
                                </div>
                            </div>
                            <div
                                v-if="validation.validUrl"
                                class="px-2 py-2"
                            >
                                <div
                                    ref="previewContainer"
                                    class="flex min-h-64 items-center justify-around overflow-auto border border-gray-200 p-2"
                                >
                                    <img
                                        :src="buildEditorSource()"
                                        :alt="imageAlt"
                                        :style="previewImageStyle()"
                                    />
                                </div>
                            </div>
                            <div class="block px-2 py-2 text-right">
                                <ActionButton
                                    :value="t('buttonInsert')"
                                    state="primary"
                                    :icon="MdiImagePlus"
                                    :is-disabled="!validation.validUrl"
                                    @click="handleInsertClick()"
                                />
                            </div>
                            <!-- <div class="flex">
                                <div class="flex-1 focus:outline-hidden">
                                    <div class="mx-auto max-w-7xl px-2 pt-12 sm:px-4 md:pt-4">
                                        <div class="py-2">
                                            {{
                                                t('imageListPreviewText', {
                                                    count:
                                                        typeof directory.content === 'object'
                                                            ? directory.content.length
                                                            : 0,
                                                })
                                            }}
                                        </div>

                                        <div
                                            v-if="directory.open"
                                            class="grid grid-cols-1 gap-4 md:grid-cols-4 lg:grid-cols-6"
                                        >
                                            <div
                                                v-for="(image, index) in directory.content"
                                                :key="index"
                                                class="group/folder flex cursor-pointer flex-col justify-between rounded-md border border-gray-200 p-4 shadow-sm hover:bg-gray-100"
                                                @click="insertImage(image)"
                                            >
                                                <div class="j">
                                                    <img
                                                        :src="image"
                                                        alt="Resulting pic"
                                                    />
                                                </div>
                                                <div
                                                    class="j text-sm font-semibold break-words text-gray-700 group-hover/folder:text-blue-600"
                                                >
                                                    {{ extractFilename(image) }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div> -->
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { reactive, ref, watch } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import ActionButton from '../ui/ActionButton.vue';
import AlertBox from '../ui/AlertBox.vue';
import { t } from '../composables/i18n';
import { imagePlaceholderSrc } from '../composables/useImage';
import {
    MdiImage,
    MdiClose,
    MdiImagePlus,
    MdiAlert,
    MdiOpenInApp,
} from 'materialdesignicons-vue3/icons/';
import InsertImageCodeStatic from './InsertImageCodeStatic.vue';

defineExpose({
    name: 'ImageInsert',
});

const props = defineProps({
    state: {
        type: Object,
        default: () => {
            return {
                open: false,
                title: '',
                text: '',
                data: {},
                overlayPreventClose: false,
            };
        },
    },
});

const state = props.state;
const allowedProtocols = ['placeholder:', 'https://', 'http://', 'data:'];

if (state.data.type === 'EMAIL') {
    // here we remove data protocol for emails, bcause email clients do not support data uris and it is not needed for email templates, where images are usually hosted externally
    allowedProtocols.splice(allowedProtocols.indexOf('data:'), 1);
}
const detectProtocol = (link) => {
    const dataUriRegex = /^data:([\w/+]+);base64,([\w+/=]+)$/;
    const httpLinkRegex = /^http:\/\/([\da-z.-]+)\.([a-z.]{2,20})([/\w .-]*)(\?.*)?\/?$/;
    const httpsLinkRegex = /^https:\/\/([\da-z.-]+)\.([a-z.]{2,20})([/\w .-]*)(\?.*)?\/?$/;
    const placeholderRegex =
        /^\[=(item\.[a-zA-Z_]\w*|([a-zA-Z_][\w]*)(?:\[\d+\])\.([a-zA-Z_]\w*))\s*\]$/;
    const protocol = {
        name: 'placeholder:',
        default: 'placeholder:',
        detected: false,
        input: link,
    };

    if (httpsLinkRegex.test(link)) {
        protocol.name = 'https://';
        protocol.detected = true;
    } else if (httpLinkRegex.test(link)) {
        protocol.name = 'http://';
        protocol.detected = true;
    } else if (placeholderRegex.test(link)) {
        protocol.name = 'placeholder:';
        protocol.detected = true;
    } else if (dataUriRegex.test(link)) {
        protocol.name = 'data:';
        protocol.detected = true;
    }
    return protocol;
};

const imageSource = ref('');
const imageAlt = ref('');
const imageWidth = ref(300);
const imageHeight = ref(225);
const imageAspect = ref(160 / 120);
const dimensionLoadId = ref(0);
const currentImagePosition = ref(null);
const previewContainer = ref(null);
const defaultProtocol = reactive(detectProtocol(imageSource.value));
const validation = reactive({
    validUrl: false,
});

const maxImageWidth = () => {
    return state.data.type === 'EMAIL' ? 1000 : 740;
};

const clampImageWidth = () => {
    const width = parseInt(imageWidth.value);

    if (width > maxImageWidth()) {
        imageWidth.value = maxImageWidth();
    }
};

const buildImageSource = () => {
    const source = imageSource.value.trim();

    if (defaultProtocol.name === 'placeholder:') {
        return source.replace(/^placeholder:/, '');
    }

    if (defaultProtocol.name === 'data:') {
        return source.startsWith('data:') ? source : `data:${source}`;
    }

    return `${defaultProtocol.name}${source.replace(/^https?:\/\//, '')}`;
};

const buildEditorSource = () => {
    return defaultProtocol.name === 'placeholder:'
        ? imagePlaceholderSrc(imageWidth.value, imageHeight.value)
        : buildImageSource();
};

const previewImageStyle = () => {
    const width = parseInt(imageWidth.value) || 300;
    const containerWidth = previewContainer.value?.clientWidth || 0;

    return {
        maxWidth: '100%',
        width: containerWidth && width < containerWidth ? `${width}px` : '100%',
        height: 'auto',
        objectFit: 'contain',
    };
};

const buildImageStyle = () => {
    const width = parseInt(imageWidth.value) || 300;

    if (defaultProtocol.name === 'placeholder:') {
        return `width: ${width}px;`;
    }

    return `width: ${width}px; height: ${parseInt(imageHeight.value) || 225}px;`;
};

const imageToDataUrl = async (source) => {
    const response = await fetch(source);
    const blob = await response.blob();

    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            resolve(reader.result);
        };
        reader.readAsDataURL(blob);
    });
};

const loadImageDimensions = (source) => {
    return new Promise((resolve) => {
        const image = new Image();
        image.onload = () => {
            resolve({
                width: image.naturalWidth,
                height: image.naturalHeight,
            });
        };
        image.onerror = () => {
            resolve({
                width: 0,
                height: 0,
            });
        };
        image.src = source;
    });
};

const buildInsertEditorSource = async (originSrc) => {
    if (defaultProtocol.name === 'placeholder:') {
        return imagePlaceholderSrc(imageWidth.value, imageHeight.value);
    }

    if (state.data.type === 'PDF' && ['http://', 'https://'].includes(defaultProtocol.name)) {
        return await imageToDataUrl(originSrc);
    }

    return originSrc;
};

const chooseFile = function () {
    document.querySelector('#image-file').click();
};

const loadImageFile = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
        return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
        defaultProtocol.name = 'data:';
        imageSource.value = reader.result || '';

        if (imageAlt.value.trim().length === 0) {
            imageAlt.value = file.name;
        }

        syncProtocol();
        syncImageDimensions();
        validation.validUrl = isImageInsertValid();
    };
    reader.readAsDataURL(file);
};

const applyGeneratedImageCode = (imageCode) => {
    const width = parseInt(imageWidth.value) || 300;
    const defaultWidth = parseInt(imageCode.defaultWidth) || width;
    const defaultHeight = parseInt(imageCode.defaultHeight) || 225;
    const aspect = defaultWidth > 0 && defaultHeight > 0 ? defaultWidth / defaultHeight : 1;

    defaultProtocol.name = 'data:';
    defaultProtocol.detected = true;
    imageSource.value = imageCode.src || '';
    imageWidth.value = width;
    imageHeight.value = Math.round(width / aspect);
    imageAspect.value = aspect;

    if (imageAlt.value.trim().length === 0) {
        imageAlt.value = imageCode.alt || t('placeholderTypeImageCode');
    }

    validation.validUrl = isImageInsertValid();
};

const parseImageStyleSize = (style) => {
    const width = `${style || ''}`.match(/width:\s*(\d+)px/i);
    const height = `${style || ''}`.match(/height:\s*(\d+)px/i);

    return {
        width: width ? parseInt(width[1]) : 300,
        height: height ? parseInt(height[1]) : 225,
        hasWidth: Boolean(width),
        hasHeight: Boolean(height),
    };
};

const getCurrentImage = (editor) => {
    const selection = editor.state.selection;

    if (selection.node?.type?.name === 'image') {
        return {
            attrs: selection.node.attrs,
            pos: selection.from,
        };
    }

    return null;
};

const stripProtocol = (source, protocol) => {
    if (protocol === 'placeholder:') {
        return source.replace(/^placeholder:/, '');
    }

    if (protocol === 'data:') {
        return source.replace(/^data:/, '');
    }

    return source.replace(/^https?:\/\//, '');
};

const fillFormFromImage = async (image) => {
    const originSrc =
        image.attrs['data-origin-src'] || image.attrs['data-origin-url'] || image.attrs.src || '';
    const protocol = detectProtocol(originSrc);
    const size = parseImageStyleSize(image.attrs.style);
    const editorWidth = parseInt(image.attrs['data-editor-width']);
    const editorHeight = parseInt(image.attrs['data-editor-height']);
    const isPlaceholder = protocol.detected && protocol.name === 'placeholder:';
    const dimensions =
        isPlaceholder || (editorWidth && editorHeight) || (size.hasWidth && size.hasHeight)
            ? {
                  width: 0,
                  height: 0,
              }
            : await loadImageDimensions(image.attrs.src || originSrc);

    defaultProtocol.name = protocol.detected ? protocol.name : allowedProtocols[0];
    defaultProtocol.detected = protocol.detected;
    imageSource.value = stripProtocol(originSrc, defaultProtocol.name);
    imageAlt.value = image.attrs.alt || '';
    imageWidth.value = editorWidth || (size.hasWidth ? size.width : dimensions.width || size.width);
    clampImageWidth();
    imageHeight.value =
        editorHeight || (size.hasHeight ? size.height : dimensions.height || size.height);
    if (dimensions.width > 0 && dimensions.height > 0) {
        imageAspect.value = dimensions.width / dimensions.height;
    } else if (parseInt(imageWidth.value) > 0 && parseInt(imageHeight.value) > 0) {
        imageAspect.value = parseInt(imageWidth.value) / parseInt(imageHeight.value);
    }
    currentImagePosition.value = image.pos;
    validation.validUrl = isImageInsertValid();
};

const syncImageHeight = () => {
    if (defaultProtocol.name === 'placeholder:') {
        return;
    }

    const width = parseInt(imageWidth.value);

    if (!width || !imageAspect.value) {
        return;
    }

    imageHeight.value = Math.round(width / imageAspect.value);
};

const syncImageDimensions = () => {
    dimensionLoadId.value += 1;
    const loadId = dimensionLoadId.value;

    if (defaultProtocol.name === 'placeholder:') {
        return;
    }

    const source = buildEditorSource();

    if (!source || !defaultProtocol.detected) {
        return;
    }

    const image = new Image();
    image.onload = () => {
        if (loadId !== dimensionLoadId.value || !image.naturalWidth || !image.naturalHeight) {
            return;
        }

        imageAspect.value = image.naturalWidth / image.naturalHeight;
        syncImageHeight();
    };
    image.src = source;
};

const isImageInsertValid = () => {
    return (
        defaultProtocol.detected &&
        imageAlt.value.trim().length > 0 &&
        parseInt(imageWidth.value) > 0 &&
        parseInt(imageHeight.value) > 0
    );
};

const syncProtocol = () => {
    const source = buildImageSource();
    const protocol = detectProtocol(source);

    defaultProtocol.detected = protocol.detected;
    defaultProtocol.name = protocol.detected ? protocol.name : defaultProtocol.name;
};

watch(imageSource, () => {
    syncProtocol();
    syncImageDimensions();
    validation.validUrl = isImageInsertValid();
});
watch(imageAlt, () => {
    validation.validUrl = isImageInsertValid();
});
watch(imageWidth, () => {
    clampImageWidth();
    syncImageHeight();
    validation.validUrl = isImageInsertValid();
});
watch(imageHeight, () => {
    validation.validUrl = isImageInsertValid();
});
watch(
    () => defaultProtocol.name,
    () => {
        syncProtocol();
        syncImageDimensions();
        validation.validUrl = isImageInsertValid();
    },
);

watch(state, async () => {
    if (state.open === true) {
        const currentImage = getCurrentImage(state.data.editor);

        imageSource.value = '';
        imageAlt.value = '';
        imageWidth.value = 300;
        imageHeight.value = 225;
        imageAspect.value = 160 / 120;
        defaultProtocol.name = allowedProtocols[0];
        defaultProtocol.detected = false;
        currentImagePosition.value = null;
        validation.validUrl = false;

        if (currentImage) {
            await fillFormFromImage(currentImage);
        }
    }
});

const insertImage = async () => {
    syncProtocol();
    clampImageWidth();

    if (!isImageInsertValid()) {
        console.warn('ImageInsertSrc insertImage aborted: invalid image data', {
            defaultProtocol: {
                name: defaultProtocol.name,
                detected: defaultProtocol.detected,
            },
            imageAlt: imageAlt.value,
            imageWidth: imageWidth.value,
            imageHeight: imageHeight.value,
        });
        return;
    }

    const originSrc = buildImageSource();
    let src = originSrc;

    try {
        src = await buildInsertEditorSource(originSrc);
    } catch (error) {
        console.warn('ImageInsertSrc cannot convert image source to data URI', {
            originSrc: originSrc,
            error: error,
        });
        return;
    }

    const attrs = {
        src: src,
        'data-origin-src': originSrc,
        'data-editor-width': `${parseInt(imageWidth.value) || 300}`,
        'data-editor-height': `${parseInt(imageHeight.value) || 225}`,
        alt: imageAlt.value,
        style: buildImageStyle(),
    };
    const editor = state.data.editor;
    const htmlBefore = editor.getHTML();

    let result = false;

    if (currentImagePosition.value !== null) {
        editor
            .chain()
            .focus()
            .command(({ tr }) => {
                tr.setNodeMarkup(currentImagePosition.value, null, attrs);
                return true;
            })
            .run();
        result = true;
    } else {
        result = editor
            .chain()
            .focus()
            .insertContent({
                type: 'image',
                attrs: attrs,
            })
            .run();
    }
    const htmlAfter = editor.getHTML();

    if (currentImagePosition.value === null && (!result || htmlBefore === htmlAfter)) {
        const fallbackResult = editor.chain().focus().insertContent(renderImageHtml(attrs)).run();
        console.warn('ImageInsertSrc insertImage fallback result', {
            result: fallbackResult,
            htmlBefore: htmlBefore,
            htmlAfter: editor.getHTML(),
        });
    }

    state.open = false;
};

const handleInsertClick = async () => {
    await insertImage();
};

const escapeHtmlAttribute = (value) => {
    return `${value}`
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
};

const renderImageHtml = (attrs) => {
    return `<img src="${escapeHtmlAttribute(attrs.src)}" data-origin-src="${escapeHtmlAttribute(
        attrs['data-origin-src'],
    )}" data-editor-width="${escapeHtmlAttribute(
        attrs['data-editor-width'],
    )}" data-editor-height="${escapeHtmlAttribute(attrs['data-editor-height'])}" alt="${escapeHtmlAttribute(
        attrs.alt,
    )}" style="${escapeHtmlAttribute(attrs.style)}">`;
};

const dialogClose = (preventClose = false) => {
    if (preventClose === false) {
        state.open = false;
    }
};
</script>
